/**
 * DailyBriefingPage
 * Finance operations daily briefing — same look & feel as DashboardPage.
 * Accessible via UserMenu → "Daily Briefing" or directly at /daily-briefing.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector }  from 'react-redux';
import {
  Clock, TrendingDown, Landmark, GitMerge, BookOpen, Activity,
  AlertCircle, AlertTriangle, CheckCircle2, ArrowRight, ChevronRight,
  RefreshCw, Building2, Zap, Shield, TrendingUp, Newspaper,
} from 'lucide-react';
import {
  PieChart, Pie, Cell,
} from 'recharts';

import { dashboardApi }           from '@/api/dashboard.api';
import { useAuth }                from '@/hooks/useAuth';
import { usePermission }          from '@/hooks/usePermission';
import { AppRoutes }              from '@/constants/routes';
import { Permissions }            from '@/constants/permissions';
import { cn }                     from '@/utils/cn';
import type { RootState }         from '@/state/store';
import type { WelcomeBriefingData } from '@/types/dashboardEnterprise.types';

// ── Skeleton ──────────────────────────────────────────────────────────────────

function Skeleton({ w = 'w-24', h = 'h-4' }: { w?: string; h?: string }) {
  return <div className={cn('rounded animate-pulse bg-slate-200 dark:bg-slate-700', w, h)} />;
}

// ── Semi-circle Gauge ─────────────────────────────────────────────────────────

function SemiGauge({ pct }: { pct: number }) {
  const clamped   = Math.max(0, Math.min(100, Math.round(pct)));
  const fillColor = clamped >= 70 ? '#10b981' : clamped >= 40 ? '#f59e0b' : '#ef4444';

  const zoneData = [
    { value: 40, fill: '#fca5a5' },
    { value: 30, fill: '#fde68a' },
    { value: 30, fill: '#6ee7b7' },
  ];
  const gaugeData = [
    { value: clamped,       fill: fillColor    },
    { value: 100 - clamped, fill: 'transparent' },
  ];

  return (
    <div className="relative w-full" style={{ height: 90 }}>
      <PieChart width={140} height={90} style={{ margin: '0 auto' }}>
        <Pie data={zoneData}  cx="50%" cy="85%" startAngle={180} endAngle={0}
          innerRadius={36} outerRadius={48} paddingAngle={1} dataKey="value"
          stroke="none" isAnimationActive={false}>
          {zoneData.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Pie>
        <Pie data={[{ value: 100 }]} cx="50%" cy="85%" startAngle={180} endAngle={0}
          innerRadius={28} outerRadius={36} dataKey="value" stroke="none"
          fill="#e2e8f0" isAnimationActive={false} />
        <Pie data={gaugeData} cx="50%" cy="85%" startAngle={180} endAngle={0}
          innerRadius={28} outerRadius={47} dataKey="value" stroke="none" cornerRadius={3}>
          {gaugeData.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Pie>
      </PieChart>
      <div className="absolute bottom-0 left-0 right-0 flex justify-center" style={{ paddingBottom: 0 }}>
        <span className="text-xl font-black leading-none" style={{ color: fillColor }}>
          {clamped}%
        </span>
      </div>
    </div>
  );
}

// ── Health Card ───────────────────────────────────────────────────────────────

function HealthCard({ title, pct, description, loading }: {
  title: string; pct: number; description: string; loading: boolean;
}) {
  const clamped     = Math.max(0, Math.min(100, Math.round(pct)));
  const statusLabel = clamped >= 70 ? 'Healthy' : clamped >= 40 ? 'Caution' : 'Critical';
  const statusCls   = clamped >= 70
    ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800'
    : clamped >= 40
    ? 'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800'
    : 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800';
  const borderAccent = clamped >= 70 ? 'border-t-emerald-400'
    : clamped >= 40 ? 'border-t-amber-400' : 'border-t-red-400';

  return (
    <div className={cn(
      'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl',
      'flex flex-col items-center text-center overflow-hidden border-t-[3px]', borderAccent,
    )}>
      <div className="px-4 pt-4 pb-1 w-full">
        {loading
          ? <div className="flex justify-center py-4"><Skeleton w="w-24" h="h-12" /></div>
          : <SemiGauge pct={pct} />}
      </div>
      <div className="px-4 pb-4 w-full">
        <p className="text-sm font-bold text-slate-900 dark:text-white">{title}</p>
        {loading
          ? <Skeleton w="w-32 mx-auto" h="h-3" />
          : <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-tight">{description}</p>}
        {!loading && (
          <span className={cn('inline-flex items-center mt-2 text-xs font-semibold px-2.5 py-0.5 rounded-full', statusCls)}>
            {statusLabel}
          </span>
        )}
      </div>
    </div>
  );
}

// ── KPI Tile ──────────────────────────────────────────────────────────────────

interface KpiTileProps {
  label: string; value: string | number; sub?: string;
  subColor?: 'green' | 'red' | 'amber' | 'muted';
  icon: React.ReactNode; iconColor: string;
  loading?: boolean; onClick?: () => void;
}

function KpiTile({ label, value, sub, subColor = 'muted', icon, iconColor, loading, onClick }: KpiTileProps) {
  const subCls = {
    green: 'text-emerald-600 dark:text-emerald-400',
    red:   'text-red-600 dark:text-red-400',
    amber: 'text-amber-600 dark:text-amber-400',
    muted: 'text-slate-500 dark:text-slate-400',
  }[subColor];

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex flex-col gap-2',
        onClick && 'cursor-pointer hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-sm transition-all',
      )}
    >
      <div className="flex items-center gap-2">
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', iconColor)}>
          {icon}
        </div>
        <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight">
          {label}
        </p>
      </div>
      {loading ? <Skeleton w="w-20" h="h-7" /> : (
        <p className="text-2xl font-bold text-slate-900 dark:text-white leading-none">{value}</p>
      )}
      {!loading && sub && <p className={cn('text-[11px] leading-tight', subCls)}>{sub}</p>}
      {loading && <Skeleton w="w-28" h="h-3" />}
    </div>
  );
}

// ── Priority Action Row ───────────────────────────────────────────────────────

function PriorityRow({
  icon, label, meta, severity, onClick,
}: {
  icon: React.ReactNode; label: string; meta?: string;
  severity: 'critical' | 'warning' | 'info'; onClick: () => void;
}) {
  const dot  = severity === 'critical' ? 'bg-red-500' : severity === 'warning' ? 'bg-amber-400' : 'bg-blue-400';
  const left = severity === 'critical'
    ? 'border-l-red-500'  : severity === 'warning'
    ? 'border-l-amber-400' : 'border-l-blue-400';

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 text-left',
        'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl',
        'border-l-4 hover:shadow-sm hover:border-slate-300 dark:hover:border-slate-600',
        'transition-all group', left,
      )}
    >
      <span className={cn('w-2 h-2 rounded-full shrink-0', dot)} />
      <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{label}</p>
        {meta && <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{meta}</p>}
      </div>
      <ArrowRight size={14} className="text-slate-400 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export const DailyBriefingPage = () => {
  const navigate          = useNavigate();
  const { user }          = useAuth();
  const { hasPermission } = usePermission();
  const session           = useSelector((s: RootState) => s.session.session);

  const [data,    setData]    = useState<WelcomeBriefingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    dashboardApi.getWelcomeBriefing()
      .then(res => setData(res.data?.data ?? null))
      .catch(() => setError('Failed to load briefing data. Please retry.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const now       = new Date();
  const hour      = now.getHours();
  const greeting  = hour < 5  ? 'Good night'    : hour < 12 ? 'Good morning'
                  : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.firstName ?? user?.fullName?.split(' ')[0] ?? 'there';
  const roles     = user?.roles ?? [];
  const roleLabel = roles.includes('Admin') ? 'System Administrator'
                  : roles.includes('CFO')   ? 'Chief Financial Officer'
                  : roles.includes('Finance Manager') ? 'Finance Manager'
                  : roles.includes('Accountant')      ? 'Accountant'
                  : roles[0] ?? 'Finance User';

  const totalPending  = data?.totalPendingApprovals ?? 0;
  const overdueCount  = data?.overdueApprovalsCount  ?? 0;
  const criticalCount = (data?.criticalAlerts ?? []).filter(a => a.severity === 'critical').length;
  const overallStatus = criticalCount > 0 ? 'critical' : totalPending > 5 ? 'warn' : 'ok';

  const avgHealth = data
    ? (data.workflowHealthScore + data.arHealthScore + data.apHealthScore + data.bankingHealthScore) / 4
    : 0;
  const healthLabel = avgHealth >= 80 ? 'Healthy' : avgHealth >= 55 ? 'Attention needed' : 'Requires action';

  const healthMetrics = [
    { label: 'Workflow',    score: data?.workflowHealthScore ?? 0, icon: <Activity size={14} />,    desc: `${totalPending} pending · ${overdueCount} overdue` },
    { label: 'Receivables', score: data?.arHealthScore        ?? 0, icon: <TrendingUp size={14} />,  desc: `${data?.arOverdueCount ?? 0} overdue invoices` },
    { label: 'Payables',    score: data?.apHealthScore        ?? 0, icon: <Landmark size={14} />,   desc: `${data?.apDueSoonCount ?? 0} due within 3 days` },
    { label: 'Banking',     score: data?.bankingHealthScore   ?? 0, icon: <Shield size={14} />,     desc: `${data?.unreconciledBankCount ?? 0} unreconciled accounts` },
  ];

  // Priority actions (permission-gated)
  type PItem = { icon: React.ReactNode; label: string; meta?: string; severity: 'critical' | 'warning' | 'info'; route: string; show: boolean };
  const priorityActions: PItem[] = [
    {
      icon: <Clock size={14} />,
      label: `Review ${totalPending} pending approval${totalPending !== 1 ? 's' : ''}`,
      meta:  overdueCount > 0 ? `${overdueCount} overdue >48h — urgent` : 'Awaiting your action',
      severity: (overdueCount > 0 ? 'critical' : 'warning') as 'critical' | 'warning',
      route: AppRoutes.GL_APPROVAL_INBOX,
      show:  totalPending > 0 && hasPermission(Permissions.GL_JOURNAL_APPROVE),
    },
    {
      icon: <TrendingDown size={14} />,
      label: `${data?.arOverdueCount ?? 0} overdue receivable${(data?.arOverdueCount ?? 0) !== 1 ? 's' : ''}`,
      meta:  data?.arOverdueAmount ? `${data.arOverdueAmount.toLocaleString('en-US', { style: 'currency', currency: session?.companyCurrency ?? 'USD', maximumFractionDigits: 0 })} outstanding` : undefined,
      severity: ((data?.arOverdueCount ?? 0) > 5 ? 'critical' : 'warning') as 'critical' | 'warning',
      route: AppRoutes.AR_INVOICES,
      show:  (data?.arOverdueCount ?? 0) > 0 && hasPermission(Permissions.AR_INVOICE_VIEW),
    },
    {
      icon: <Landmark size={14} />,
      label: `${data?.apDueSoonCount ?? 0} AP payment${(data?.apDueSoonCount ?? 0) !== 1 ? 's' : ''} due in 3 days`,
      meta:  data?.apDueSoonAmount ? `${data.apDueSoonAmount.toLocaleString('en-US', { style: 'currency', currency: session?.companyCurrency ?? 'USD', maximumFractionDigits: 0 })} payable` : undefined,
      severity: ((data?.apDueSoonCount ?? 0) > 3 ? 'critical' : 'warning') as 'critical' | 'warning',
      route: AppRoutes.AP_BILLS,
      show:  (data?.apDueSoonCount ?? 0) > 0 && hasPermission(Permissions.AP_BILL_VIEW),
    },
    {
      icon: <GitMerge size={14} />,
      label: `${data?.unreconciledBankCount ?? 0} bank account${(data?.unreconciledBankCount ?? 0) !== 1 ? 's' : ''} pending reconciliation`,
      meta:  'Review unreconciled transactions',
      severity: 'warning' as const,
      route: AppRoutes.BANK_RECONCILE,
      show:  (data?.unreconciledBankCount ?? 0) > 0 && hasPermission(Permissions.BNK_RECONCILE_VIEW),
    },
    {
      icon: <BookOpen size={14} />,
      label: `${data?.glJournalsMtd ?? 0} GL journals this month`,
      meta:  data?.glPostedToday ? `${data.glPostedToday} posted today` : 'Review journal activity',
      severity: 'info' as const,
      route: AppRoutes.GL_JOURNALS,
      show:  hasPermission(Permissions.GL_JOURNAL_VIEW),
    },
  ].filter(a => a.show);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6 pb-8">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 rounded-xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Newspaper size={20} className="text-teal-500" />
              Daily Briefing
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {greeting}, <span className="font-semibold text-slate-700 dark:text-slate-200">{firstName}</span>
              {' '}· {roleLabel}
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
              {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              {' · '}
              {now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              {session?.companyName && ` · ${session.companyName}`}
              {session?.financialYearCode && ` · FY ${session.financialYearCode}`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Overall status badge */}
            {!loading && (
              <div className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border',
                overallStatus === 'critical'
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                  : overallStatus === 'warn'
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400',
              )}>
                {overallStatus === 'critical'
                  ? <AlertCircle size={13} />
                  : overallStatus === 'warn'
                  ? <AlertTriangle size={13} />
                  : <CheckCircle2 size={13} />}
                {overallStatus === 'critical'
                  ? `${criticalCount} critical item${criticalCount !== 1 ? 's' : ''}`
                  : overallStatus === 'warn'
                  ? `${totalPending} items need attention`
                  : `${healthLabel}`}
              </div>
            )}
            <button
              onClick={load}
              disabled={loading}
              title="Refresh"
              className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
          <AlertCircle size={15} />
          {error}
        </div>
      )}

      {/* ── Operations Snapshot — 6 KPI tiles ──────────────────────────────── */}
      <div>
        <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Zap size={13} className="text-teal-500" />
          Operations Snapshot
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          <KpiTile
            label="Pending Approvals"
            value={loading ? '—' : totalPending}
            sub={overdueCount > 0 ? `${overdueCount} overdue >48h` : 'All within time'}
            subColor={totalPending === 0 ? 'green' : overdueCount > 0 ? 'red' : 'amber'}
            icon={<Clock size={16} className="text-amber-600" />}
            iconColor="bg-amber-100 dark:bg-amber-900/30"
            loading={loading}
            onClick={hasPermission(Permissions.GL_JOURNAL_APPROVE) ? () => navigate(AppRoutes.GL_APPROVAL_INBOX) : undefined}
          />
          <KpiTile
            label="AR Overdue"
            value={loading ? '—' : (data?.arOverdueCount ?? 0)}
            sub="Past-due invoices"
            subColor={(data?.arOverdueCount ?? 0) === 0 ? 'green' : 'red'}
            icon={<TrendingDown size={16} className="text-rose-600" />}
            iconColor="bg-rose-100 dark:bg-rose-900/30"
            loading={loading}
            onClick={hasPermission(Permissions.AR_INVOICE_VIEW) ? () => navigate(AppRoutes.AR_INVOICES) : undefined}
          />
          <KpiTile
            label="AP Due Soon"
            value={loading ? '—' : (data?.apDueSoonCount ?? 0)}
            sub="Within 3 days"
            subColor={(data?.apDueSoonCount ?? 0) === 0 ? 'green' : 'amber'}
            icon={<Landmark size={16} className="text-orange-600" />}
            iconColor="bg-orange-100 dark:bg-orange-900/30"
            loading={loading}
            onClick={hasPermission(Permissions.AP_BILL_VIEW) ? () => navigate(AppRoutes.AP_BILLS) : undefined}
          />
          <KpiTile
            label="Bank Recon"
            value={loading ? '—' : (data?.unreconciledBankCount ?? 0) === 0 ? 'All Clear' : `${data?.unreconciledBankCount} pending`}
            sub={(data?.unreconciledBankCount ?? 0) === 0 ? 'All accounts reconciled ✓' : 'Reconciliation required'}
            subColor={(data?.unreconciledBankCount ?? 0) === 0 ? 'green' : 'amber'}
            icon={<Building2 size={16} className="text-sky-600" />}
            iconColor="bg-sky-100 dark:bg-sky-900/30"
            loading={loading}
            onClick={hasPermission(Permissions.BNK_RECONCILE_VIEW) ? () => navigate(AppRoutes.BANK_RECONCILE) : undefined}
          />
          <KpiTile
            label="GL Journals MTD"
            value={loading ? '—' : (data?.glJournalsMtd ?? 0)}
            sub={data?.glPostedToday ? `${data.glPostedToday} posted today` : 'Month to date'}
            subColor="muted"
            icon={<BookOpen size={16} className="text-violet-600" />}
            iconColor="bg-violet-100 dark:bg-violet-900/30"
            loading={loading}
            onClick={hasPermission(Permissions.GL_JOURNAL_VIEW) ? () => navigate(AppRoutes.GL_JOURNALS) : undefined}
          />
          <KpiTile
            label="Health Score"
            value={loading ? '—' : `${Math.round(avgHealth)}%`}
            sub={healthLabel}
            subColor={avgHealth >= 70 ? 'green' : avgHealth >= 40 ? 'amber' : 'red'}
            icon={<Activity size={16} className="text-teal-600" />}
            iconColor="bg-teal-100 dark:bg-teal-900/30"
            loading={loading}
          />
        </div>
      </div>

      {/* ── Financial Health Scores ─────────────────────────────────────────── */}
      <div>
        <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Activity size={13} className="text-teal-500" />
          Financial Health
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {healthMetrics.map(m => (
            <HealthCard
              key={m.label}
              title={m.label}
              pct={m.score}
              description={m.desc}
              loading={loading}
            />
          ))}
        </div>
      </div>

      {/* ── Lower 2-col: Priority Actions + Critical Alerts ────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        {/* Priority Actions */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={15} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-900 dark:text-white">Priority Actions</span>
            </div>
            {!loading && (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {priorityActions.length} item{priorityActions.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="p-4 space-y-2">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-14 rounded-xl bg-slate-100 dark:bg-slate-700/40 animate-pulse" />
              ))
            ) : priorityActions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400 gap-2">
                <CheckCircle2 size={32} className="text-emerald-400" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">No priority actions</p>
                <p className="text-xs">Everything is up to date</p>
              </div>
            ) : (
              priorityActions.map((a, i) => (
                <PriorityRow
                  key={a.route + i}
                  icon={a.icon}
                  label={a.label}
                  meta={a.meta}
                  severity={a.severity}
                  onClick={() => navigate(a.route)}
                />
              ))
            )}
          </div>
        </div>

        {/* Critical Alerts */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle size={15} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-900 dark:text-white">Critical Alerts</span>
            </div>
            {!loading && (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {(data?.criticalAlerts ?? []).length} alert{(data?.criticalAlerts ?? []).length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="px-4 py-3 flex gap-3">
                  <Skeleton w="w-4" h="h-4" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton w="w-48" h="h-4" />
                    <Skeleton w="w-64" h="h-3" />
                  </div>
                </div>
              ))
            ) : (data?.criticalAlerts ?? []).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400 gap-2">
                <CheckCircle2 size={32} className="text-emerald-400" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">All clear — no alerts</p>
                <p className="text-xs">Finance operations look healthy.</p>
              </div>
            ) : (
              (data?.criticalAlerts ?? []).map((alert, i) => (
                <div
                  key={i}
                  onClick={() => alert.actionRoute && navigate(alert.actionRoute)}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 border-l-4 transition-colors',
                    alert.severity === 'critical' ? 'border-l-red-500'  : 'border-l-amber-400',
                    alert.actionRoute && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30',
                  )}
                >
                  {alert.severity === 'critical'
                    ? <AlertCircle size={15} className="text-red-500 mt-0.5 shrink-0" />
                    : <AlertTriangle size={15} className="text-amber-500 mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{alert.title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{alert.body}</p>
                  </div>
                  {alert.actionRoute && (
                    <ChevronRight size={14} className="text-slate-400 mt-0.5 shrink-0" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Footer — go to dashboard ─────────────────────────────────────────── */}
      <div className="flex justify-end pt-2">
        <button
          onClick={() => navigate(AppRoutes.DASHBOARD)}
          className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          Go to Dashboard
          <ArrowRight size={15} />
        </button>
      </div>

    </div>
  );
};
